/**
 * Small shared UI primitives. Deliberately plain — they wrap the class names
 * defined in styles.css rather than introducing a component framework.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export function Card({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return <div className="card" style={style}>{children}</div>;
}

export function SectionTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
      <h3 style={{
        color: 'var(--ha-text-muted)', fontSize: '0.875rem', textTransform: 'uppercase',
        letterSpacing: '0.05em', margin: 0,
      }}>{children}</h3>
      {actions}
    </div>
  );
}

export function Pill({ tone = 'muted', title, children }: {
  tone?: 'muted' | 'emerald' | 'red';
  title?: string;
  children: ReactNode;
}) {
  return <span className={`pill pill-${tone}`} title={title}>{children}</span>;
}

export function Alert({ tone, children }: { tone: 'error' | 'warn' | 'success'; children: ReactNode }) {
  return <div className={`alert-${tone}`} style={{ fontSize: '0.85rem' }}>{children}</div>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <p className="muted" style={{ fontSize: '0.875rem' }}>{label}</p>;
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="muted" style={{ padding: '1rem 0.4rem' }}>{children}</td>
    </tr>
  );
}

/** Coloured status dot + label, matching the old statusColor helper. */
export function StateDot({ state }: { state: string }) {
  const color =
    state === 'running' ? 'var(--ha-accent-emerald)'
    : state === 'exited' || state === 'stopped' || state === 'created' ? 'var(--ha-text-muted)'
    : state === 'failed' || state === 'error' ? 'var(--ha-accent-red)'
    : 'var(--ha-warn)';
  return <span style={{ color }}>● {state}</span>;
}

/**
 * The house action-button behaviour: disable, swap the label while running,
 * flash the outcome, then restore. Keeps every destructive action legible
 * without a toast system.
 */
export function ActionButton({
  onClick, busyLabel, children, className = 'btn-ghost btn-sm', danger, disabled, title, confirm,
}: {
  onClick: () => Promise<unknown>;
  busyLabel?: string;
  children: ReactNode;
  className?: string;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  confirm?: string;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'fail'>('idle');
  const [failTitle, setFailTitle] = useState<string | undefined>();
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const run = useCallback(async () => {
    if (confirm && !window.confirm(confirm)) return;
    setState('busy');
    try {
      await onClick();
      setState('ok');
    } catch (err) {
      setState('fail');
      setFailTitle(err instanceof Error ? err.message : String(err));
    } finally {
      timer.current = window.setTimeout(() => setState('idle'), 2500);
    }
  }, [confirm, onClick]);

  const label = state === 'busy' ? (busyLabel ?? 'Working…')
    : state === 'ok' ? 'Done'
    : state === 'fail' ? 'Failed'
    : children;

  const color = state === 'ok' ? 'var(--ha-accent-emerald)'
    : state === 'fail' ? 'var(--ha-accent-red)'
    : undefined;

  return (
    <button
      type="button"
      className={`${className}${danger ? ' btn-danger' : ''}`}
      style={color ? { color } : undefined}
      disabled={disabled || state === 'busy'}
      title={state === 'fail' ? failTitle : title}
      onClick={() => { void run(); }}
    >
      {label}
    </button>
  );
}

/** Transient inline status line; clears itself so stale messages don't linger. */
export function useFlash(timeoutMs = 6000) {
  const [flash, setFlash] = useState<{ text: string; tone: 'ok' | 'error' | 'warn' } | null>(null);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const show = useCallback((text: string, tone: 'ok' | 'error' | 'warn' = 'ok') => {
    window.clearTimeout(timer.current);
    setFlash({ text, tone });
    timer.current = window.setTimeout(() => setFlash(null), timeoutMs);
  }, [timeoutMs]);

  const node = flash ? (
    <span style={{
      fontSize: '0.8rem',
      whiteSpace: 'pre-wrap',
      color: flash.tone === 'ok' ? 'var(--ha-accent-emerald)'
        : flash.tone === 'warn' ? 'var(--ha-warn)'
        : 'var(--ha-accent-red)',
    }}>{flash.text}</span>
  ) : null;

  return { show, node };
}

/** Collapsible panel used for the "＋ Create …" forms. */
export function Collapsible({ label, open, onToggle, children }: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <>
      <button type="button" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} onClick={onToggle}>
        {label}
      </button>
      {open && (
        <div style={{
          width: '100%', marginTop: '0.75rem', padding: '1rem',
          background: 'var(--ha-bg-elevated)', borderRadius: '0.375rem',
        }}>
          {children}
        </div>
      )}
    </>
  );
}

/** Monospace editor with Tab inserting spaces — YAML and Caddyfiles need it. */
export function CodeEditor({ value, onChange, rows = 16, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      className="form-input mono"
      spellCheck={false}
      rows={rows}
      value={value}
      placeholder={placeholder}
      style={{ whiteSpace: 'pre', fontSize: '0.8rem', lineHeight: 1.5 }}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key !== 'Tab') return;
        e.preventDefault();
        const el = e.currentTarget;
        const { selectionStart: s, selectionEnd: en } = el;
        const next = `${value.slice(0, s)}  ${value.slice(en)}`;
        onChange(next);
        requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function fmtBytes(n: number | null | undefined): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
  return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

/** `seconds` is a Unix timestamp in seconds. */
export function fmtAgo(seconds: number | null | undefined): string {
  if (!seconds) return '-';
  const d = Math.floor(Date.now() / 1000) - seconds;
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
}
